import type { Reference } from '@medplum/fhirtypes'
import {
  draftPharmacyFromStamp,
  previousDispenserNcpdpidFromExternalJson,
} from '@seen/core/elationDraftPharmacy'
import type { JsonObject } from '@seen/core/json'
import {
  type ExtendedMedicationRequest,
  ExtendedMedicationRequest as MedicationRequest,
} from '@seen/core/models/medicationRequest'
import { useMemo } from 'react'
import { useSearch } from '#src/hooks/useSearch.ts'

function parseExternalMedicationOrder(
  externalResourceJSON: string | undefined
): JsonObject | null {
  if (!externalResourceJSON) {
    return null
  }

  try {
    const parsed = JSON.parse(externalResourceJSON) as unknown
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    return parsed as JsonObject
  } catch {
    return null
  }
}

export function getMedicationThreadId(
  medication: Pick<
    ExtendedMedicationRequest,
    'threadId' | 'externalResourceJSON'
  >
): string | undefined {
  if (medication.threadId) {
    return medication.threadId
  }

  const parsedOrder = parseExternalMedicationOrder(
    medication.externalResourceJSON
  )
  const thread = parsedOrder?.thread
  if (!thread || typeof thread !== 'object') {
    return undefined
  }

  const threadId = (thread as JsonObject).id
  if (typeof threadId === 'string' || typeof threadId === 'number') {
    return threadId.toString()
  }

  return undefined
}

export function getMedicationDraftId(
  medication: Pick<
    ExtendedMedicationRequest,
    'elationId' | 'externalResourceJSON'
  >
): number | string | undefined {
  if (medication.elationId !== undefined) {
    return medication.elationId
  }

  const parsedOrder = parseExternalMedicationOrder(
    medication.externalResourceJSON
  )
  const draftId = parsedOrder?.id
  if (typeof draftId === 'string' || typeof draftId === 'number') {
    return draftId
  }

  return undefined
}

export function combineDraftIdsByThread({
  serverDraftsByThreadId,
  optimisticDraftIdsByThreadId,
}: {
  serverDraftsByThreadId: ReadonlyMap<string, ExtendedMedicationRequest>
  optimisticDraftIdsByThreadId?: ReadonlyMap<string, number | string>
}): Map<string, number | string> {
  const combined = new Map<string, number | string>()

  for (const [threadId, draftMedication] of serverDraftsByThreadId.entries()) {
    const draftId = getMedicationDraftId(draftMedication)
    if (draftId !== undefined) {
      combined.set(threadId, draftId)
    }
  }

  if (optimisticDraftIdsByThreadId) {
    for (const [threadId, draftId] of optimisticDraftIdsByThreadId.entries()) {
      combined.set(threadId, draftId)
    }
  }

  return combined
}

function draftCarriesPharmacy(
  medication: Pick<
    ExtendedMedicationRequest,
    'previousDispenser' | 'externalResourceJSON'
  >
): boolean {
  return (
    draftPharmacyFromStamp(medication.previousDispenser) !== undefined ||
    previousDispenserNcpdpidFromExternalJson(
      medication.externalResourceJSON
    ) !== undefined
  )
}

/* A stamped draft, or one that already has fulfillment, wins. An older
 * draft with neither pharmacy source falls back to the source medication. */
function pharmacyMedicationForOpen(
  draft: ExtendedMedicationRequest,
  source: ExtendedMedicationRequest | undefined
): ExtendedMedicationRequest {
  if (draftCarriesPharmacy(draft)) {
    return draft
  }
  return source ?? draft
}

/* Sign on the medications page and facesheet passes the source request.
 * The ZIP-qualified search text lives on the draft stamp, so open that. */
export function medicationForDraftOpen({
  draftId,
  medication,
  draftsByThreadId,
}: {
  draftId: number | string
  medication?: ExtendedMedicationRequest
  draftsByThreadId: ReadonlyMap<string, ExtendedMedicationRequest>
}): ExtendedMedicationRequest | undefined {
  const wanted = String(draftId)
  if (medication) {
    const threadId = getMedicationThreadId(medication)
    const draft = threadId ? draftsByThreadId.get(threadId) : undefined
    if (draft && String(getMedicationDraftId(draft)) === wanted) {
      return pharmacyMedicationForOpen(draft, medication)
    }
  }
  for (const draft of draftsByThreadId.values()) {
    if (String(getMedicationDraftId(draft)) === wanted) {
      return pharmacyMedicationForOpen(draft, medication)
    }
  }
  return medication
}

export interface UseMedicationDraftsByThreadResult {
  draftByThreadId: Map<string, ExtendedMedicationRequest>
  hasDrafts: boolean
  isLoading: boolean
}

export function useMedicationDraftsByThread(
  patientReference: string | Reference | undefined
): UseMedicationDraftsByThreadResult {
  const patientReferenceString =
    typeof patientReference === 'string'
      ? patientReference
      : patientReference?.reference

  const { data: draftRequests = [], isLoading } = useSearch(
    MedicationRequest,
    patientReferenceString
      ? {
          patient: patientReferenceString,
          status: 'draft',
          'status:not': 'entered-in-error',
          _sort: '-_lastUpdated',
          _count: 1000,
        }
      : undefined
  )

  const draftByThreadId = useMemo(() => {
    const byThread = new Map<string, ExtendedMedicationRequest>()

    /* Query is sorted by -_lastUpdated, so first draft seen per thread is newest */
    for (const draftMedication of draftRequests) {
      const threadId = getMedicationThreadId(draftMedication)
      const draftId = getMedicationDraftId(draftMedication)
      if (!threadId || draftId === undefined || byThread.has(threadId)) {
        continue
      }
      byThread.set(threadId, draftMedication)
    }

    return byThread
  }, [draftRequests])

  return {
    draftByThreadId,
    hasDrafts: draftByThreadId.size > 0,
    isLoading,
  }
}
